import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from 'services/api'
import { POLL_INTERVAL_MS, windowTotals } from '../StreamIngest/vocabulary'

// The flow's reads, one hook per row (README.md). A page calls a row's hook
// once and hands its answers to the row's panel and to its own sections, so
// nothing is read twice. Each hook reads again on a change of window or
// filter, polls every minute, and keeps the last answer on screen while the
// next is read: `loading` is true from a change or a refresh until its answer
// lands, never for a poll. An answer lands only if nothing was asked after
// it, so a slow 90d read cannot overwrite the 1d one asked since.

// Each call starts a read and returns whether it is still the newest asked.
const useNewest = () => {
  const asked = useRef(0)
  return useCallback(() => {
    const ask = ++asked.current
    return () => ask === asked.current
  }, [])
}

// The pipeline's three reads (reclip-api `stream/metrics/latency.py`,
// `capture.py`, `pipeline.py`), the fleet or one account, narrowed by the
// Pipeline page's seal cause and client build. `shownDays` is the window the
// answers on the page were read for, so faded numbers never wear the next
// window's words.
export const usePipelineReads = ({
  days,
  userId = null,
  sealCause = null,
  clientVersion = null,
}) => {
  const [payload, setPayload] = useState(null)
  const [capture, setCapture] = useState(null)
  const [pipeline, setPipeline] = useState(null)
  const [shownDays, setShownDays] = useState(days)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  // The last pulse's `at`, sent back as `since` so the next pulse is exactly
  // one refresh's worth of events. A ref, not state: it must not re-enter
  // `load` and start another interval when it changes.
  const pulseAt = useRef(null)
  const newest = useNewest()

  const load = useCallback(async () => {
    const current = newest()
    const params = { days, userId, sealCause, clientVersion }
    try {
      // Fetched together and cached apart server-side, so one slow fold never
      // holds the others' numbers off the page. The pipeline fold takes the
      // account but not the strata — routing is the server pass's, not how the
      // phone closed the span — and one that fails leaves its stages drawn as
      // not built rather than taking the page down.
      const [latency, health, routing] = await Promise.all([
        api.streamMetrics.getLatency(params),
        api.streamMetrics.getCapture({ ...params, since: pulseAt.current }),
        api.streamMetrics.getPipeline({ days, userId }).catch(() => null),
      ])
      if (!current()) return
      pulseAt.current = health?.pulse?.at || pulseAt.current
      setPayload(latency)
      setCapture(health)
      setPipeline(routing)
      setShownDays(days)
      setError(null)
    } catch (err) {
      if (current()) setError(err.message)
    } finally {
      if (current()) setLoading(false)
    }
  }, [newest, days, userId, sealCause, clientVersion])

  useEffect(() => {
    pulseAt.current = null
    setLoading(true)
    load()
    const timer = setInterval(load, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [load])

  const refresh = useCallback(() => {
    setLoading(true)
    load()
  }, [load])

  const totals = useMemo(() => windowTotals(payload?.days || []), [payload])

  return {
    payload,
    capture,
    pipeline,
    totals,
    shownDays,
    loading,
    error,
    refresh,
  }
}

// One tab's own read (the Loop page's Listens and Friends), made only while
// the tab is shown: `read` is the API call, given `{ days }`. It reads again
// on a change of window or of `refreshKey` (the page's refresh), keeps the
// last answer on screen while the next is read, and drops a superseded one.
export const useTabRead = (read, { days, refreshKey = 0 }) => {
  const [answer, setAnswer] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const newest = useNewest()

  useEffect(() => {
    const current = newest()
    setLoading(true)
    read({ days })
      .then((next) => {
        if (!current()) return
        setAnswer(next)
        setError(null)
      })
      .catch((err) => current() && setError(err.message))
      .finally(() => current() && setLoading(false))
  }, [newest, read, days, refreshKey])

  return { answer, error, loading }
}

// The loop's reads, fleet-wide: the delivery (reclip-api
// `stream/metrics/loop/delivery.py`, Mongo), what the pushes led to
// (`loop/pushes.py`, the event table in BigQuery), whether quotes reach
// friends (`loop/friends.py`, the event table, which the Friends tab draws
// too), and the latency answer's product lags (moment → push, push → phone). A page that already reads the
// pipeline passes its latency answer as `latency` (`null` while it loads), so
// it is not read twice; left `undefined`, it is read here. `enabled` false
// (a page narrowed to one account) reads nothing.
//
// The event table's two reads land apart, each with its own loading flag:
// BigQuery answers in seconds, or refuses, and neither may hold the delivery
// numbers off the page.
export const useLoopReads = ({ days, enabled = true, latency }) => {
  const readsLatency = latency === undefined
  const [delivery, setDelivery] = useState(null)
  const [deliveryError, setDeliveryError] = useState(null)
  const [ownLatency, setOwnLatency] = useState(null)
  const [pushes, setPushes] = useState(null)
  const [pushesError, setPushesError] = useState(null)
  const [shownDays, setShownDays] = useState(days)
  const [loading, setLoading] = useState(enabled)
  const [pushesLoading, setPushesLoading] = useState(enabled)
  const [friends, setFriends] = useState(null)
  const [friendsError, setFriendsError] = useState(null)
  const [friendsLoading, setFriendsLoading] = useState(enabled)
  const newestDelivery = useNewest()
  const newestPushes = useNewest()
  const newestFriends = useNewest()

  const loadDelivery = useCallback(async () => {
    const current = newestDelivery()
    try {
      const [answer, latencyAnswer] = await Promise.all([
        api.streamMetrics.getLoopDelivery({ days }),
        readsLatency ? api.streamMetrics.getLatency({ days }) : null,
      ])
      if (!current()) return
      setDelivery(answer)
      if (readsLatency) setOwnLatency(latencyAnswer)
      setShownDays(days)
      setDeliveryError(null)
    } catch (err) {
      if (current()) setDeliveryError(err.message)
    } finally {
      if (current()) setLoading(false)
    }
  }, [newestDelivery, days, readsLatency])

  const loadPushes = useCallback(async () => {
    const current = newestPushes()
    try {
      const answer = await api.streamMetrics.getLoopPushes({ days })
      if (!current()) return
      setPushes(answer)
      setPushesError(null)
    } catch (err) {
      if (current()) setPushesError(err.message)
    } finally {
      if (current()) setPushesLoading(false)
    }
  }, [newestPushes, days])

  const loadFriends = useCallback(async () => {
    const current = newestFriends()
    try {
      const answer = await api.streamMetrics.getLoopFriends({ days })
      if (!current()) return
      setFriends(answer)
      setFriendsError(null)
    } catch (err) {
      if (current()) setFriendsError(err.message)
    } finally {
      if (current()) setFriendsLoading(false)
    }
  }, [newestFriends, days])

  useEffect(() => {
    setLoading(enabled)
    setPushesLoading(enabled)
    setFriendsLoading(enabled)
    if (!enabled) return undefined
    loadDelivery()
    loadPushes()
    loadFriends()
    const timer = setInterval(() => {
      loadDelivery()
      loadPushes()
      loadFriends()
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [enabled, loadDelivery, loadPushes, loadFriends])

  const refresh = useCallback(() => {
    if (!enabled) return
    setLoading(true)
    setPushesLoading(true)
    setFriendsLoading(true)
    loadDelivery()
    loadPushes()
    loadFriends()
  }, [enabled, loadDelivery, loadPushes, loadFriends])

  return {
    delivery,
    deliveryError,
    pushes,
    pushesError,
    pushesLoading,
    friends,
    friendsError,
    friendsLoading,
    product: (readsLatency ? ownLatency : latency)?.product || null,
    shownDays,
    loading,
    refresh,
  }
}
